import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

class ProductInventory extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.updateInventory);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.updateInventory);
  }

  /**
   * Updates the inventory.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateInventory = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // First, try to get updated inventory from the HTML (preferred method)
    const newInventory = event.detail.data.html?.querySelector('product-inventory');

    if (newInventory) {
      // Extract status class from the new inventory element
      const newStatusMatch = newInventory.className.match(/product-inventory--(low|in_stock|out_of_stock|final_sale)/);
      const newStatus = newStatusMatch ? newStatusMatch[1] : null;
      
      // Morph the content
      morph(this, newInventory, { childrenOnly: true });
      
      // After morphing, ensure status classes are correctly applied on the root element
      // The morph only updates children, not the root element's classes
      if (newStatus) {
        // Remove all status classes
        this.classList.remove('product-inventory--low', 'product-inventory--in_stock', 'product-inventory--out_of_stock', 'product-inventory--final_sale');
        // Add the correct status class
        this.classList.add(`product-inventory--${newStatus}`);
      }
      
      // CRITICAL: Update badge visibility based on status
      // Hide badge if status is 'in_stock', show for 'low' or 'out_of_stock'
      if (newStatus === 'in_stock') {
        this.style.display = 'none';
      } else {
        this.style.display = '';
      }
      
      // Also ensure the icon class matches the status
      const icon = this.querySelector('.product-inventory__icon');
      if (icon && newStatus) {
        // Remove all icon status classes
        icon.classList.remove('product-inventory__icon-low', 'product-inventory__icon-in_stock', 'product-inventory__icon-out_of_stock');
        // Add the correct icon status class (only if not final_sale)
        if (newStatus !== 'final_sale') {
          icon.classList.add(`product-inventory__icon-${newStatus}`);
        }
      }
      
      return;
    }

    // Fallback: Update inventory directly from variant data if HTML doesn't include it
    // This ensures inventory updates even if the section doesn't include the inventory block
    const variant = event.detail.resource;
    if (!variant) return;

    const inventoryText = this.querySelector('.product-inventory__text');
    if (!inventoryText) return;

    // Get inventory settings from data attributes (set by Liquid)
    const threshold = parseInt(this.dataset.inventoryThreshold || '10', 10);
    const showQuantity = this.dataset.showInventoryQuantity === 'true';

    // Update data attributes with new variant info
    this.dataset.variantId = String(variant.id || '');
    this.dataset.variantInventoryQuantity = String(variant.inventory_quantity || 0);
    this.dataset.variantInventoryManagement = variant.inventory_management || '';
    this.dataset.variantInventoryPolicy = variant.inventory_policy || '';
    this.dataset.variantAvailable = String(variant.available || false);

    // Calculate inventory status based on variant data
    let status = 'in_stock';
    let inventoryMessage = '';

    // Get inventory quantity - ensure it's a number
    const inventoryQuantity = parseInt(variant.inventory_quantity, 10) || 0;

    if (variant.inventory_management === 'shopify') {
      if (inventoryQuantity > threshold) {
        // Inventory is above threshold - HIDE badge (variant is in stock)
        this.style.display = 'none';
        return; // Exit early - don't show badge
      } else if (inventoryQuantity > 0 && inventoryQuantity <= threshold) {
        // Low stock: show badge (even if variant.available is true)
        this.style.display = '';
        status = 'low';
        if (showQuantity) {
          const translationKey = 'content.inventory_low_stock_show_count';
          try {
            if (window.Shopify && window.Shopify.translations && window.Shopify.translations[translationKey]) {
              inventoryMessage = window.Shopify.translations[translationKey].replace('{{ count }}', inventoryQuantity);
            } else {
              inventoryMessage = `Only ${inventoryQuantity} left in stock!`;
            }
          } catch (e) {
            inventoryMessage = `Only ${inventoryQuantity} left in stock!`;
          }
        } else {
          inventoryMessage = 'Low stock';
        }
      } else {
        // No inventory (quantity is 0) - never show badge for out of stock
        this.style.display = 'none';
        return;
      }
    } else {
      // No inventory management
      if (variant.available === true) {
        this.style.display = 'none';
        return;
      }
      // Out of stock - never show badge
      this.style.display = 'none';
      return;
    }

    // Update the inventory text
    if (inventoryText) {
      inventoryText.textContent = inventoryMessage;
    }

    // Update the status class on the product-inventory element (preserve final_sale if present)
    // Remove all status classes first
    this.classList.remove('product-inventory--low', 'product-inventory--in_stock', 'product-inventory--out_of_stock');
    
    // Only update status if not final_sale
    if (!this.classList.contains('product-inventory--final_sale')) {
      // Add the correct status class
      this.classList.add(`product-inventory--${status}`);
    }
    
    // Also update the icon class to match the status
    const icon = this.querySelector('.product-inventory__icon');
    if (icon) {
      // Remove all icon status classes
      icon.classList.remove('product-inventory__icon-low', 'product-inventory__icon-in_stock', 'product-inventory__icon-out_of_stock');
      // Add the correct icon status class (only if not final_sale)
      if (!this.classList.contains('product-inventory--final_sale')) {
        icon.classList.add(`product-inventory__icon-${status}`);
      }
    }
  };
}

if (!customElements.get('product-inventory')) {
  customElements.define('product-inventory', ProductInventory);
}
